import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LockKeyhole } from 'lucide-react';
import { usePermission } from '@/Hooks/usePermission';
import { Card, CardContent } from '@/components/ui/card';
import type { CustomerRow } from '@/Pages/CRM/lib';
import type { RepeatOrderDraft } from './lib';
import PhoneLookup from './PhoneLookup';
import CustomerPanel from './CustomerPanel';
import OrderDesk from './OrderDesk';

/**
 * ADR-0021 — the call-centre desk: a fifth caller onto the existing order
 * engine, not a second one. Every write from this screen ends inside
 * `OrdersService.create`, `placedByGuest` always false, so an order placed
 * here never sits "held for waiter" — see `lib.ts`'s module comment for why
 * that state cannot occur here and must never be drawn.
 *
 * One console, not a tabbed screen: phone lookup, the caller it resolves to,
 * and the order desk are one continuous task an agent works through per
 * call, not three destinations to navigate between.
 */
export default function CallCenter() {
  const { t } = useTranslation();
  const { can } = usePermission();
  const canSearchPhone = can('customers.search_phone');

  const [customer, setCustomer] = useState<CustomerRow | null>(null);
  const [repeatDraft, setRepeatDraft] = useState<RepeatOrderDraft | null>(null);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('callCenter.title')}</h1>
        <p className="text-sm text-ink-muted">{t('callCenter.subtitle')}</p>
      </div>

      {!canSearchPhone ? (
        <Card>
          <CardContent className="flex items-start gap-3 p-5">
            <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" aria-hidden />
            <div className="space-y-1 text-sm">
              <p className="font-medium">{t('callCenter.lookup.noPermTitle')}</p>
              <p className="text-ink-muted">{t('callCenter.lookup.noPermBody')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[26rem_1fr]">
          <div className="space-y-4">
            <PhoneLookup selected={customer} onSelect={setCustomer} />
            {customer && (
              <CustomerPanel customer={customer} onRepeat={setRepeatDraft} />
            )}
          </div>
          <OrderDesk
            customer={customer}
            repeatDraft={repeatDraft}
            onRepeatConsumed={() => setRepeatDraft(null)}
          />
        </div>
      )}
    </div>
  );
}
